import { useReducedMotion } from "framer-motion";
import { memo } from "react";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

type FacePreview = {
  id: string;
  imageUrl: string;
  alt?: string;
};

type AnticipationScreenProps = {
  headline: string;
  description?: string;
  faces: FacePreview[];
  className?: string;
  testId?: string;
};

const FACE_OFFSETS = [
  { top: "8%", left: "8%" },
  { top: "12%", left: "68%" },
  { top: "72%", left: "5%" },
  { top: "75%", left: "70%" },
  { top: "78%", left: "40%" },
];

const AnticipationScreenImpl = ({
  headline,
  description,
  faces,
  className,
  testId,
}: AnticipationScreenProps) => {
  const prefersReduced = useReducedMotion();
  return (
    <section
      data-testid={testId}
      className={`relative flex flex-col items-center justify-center gap-6 py-12 ${className ?? ""}`}
      style={{ minHeight: 480 }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {faces.slice(0, FACE_OFFSETS.length).map((face, i) => (
          <div
            key={face.id}
            style={{
              position: "absolute",
              top: FACE_OFFSETS[i]?.top,
              left: FACE_OFFSETS[i]?.left,
              width: 80,
              height: 80,
              borderRadius: "9999px",
              overflow: "hidden",
              background: "hsl(var(--brand-bg-foreground) / 0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              animation: prefersReduced
                ? undefined
                : `fc-fade-in-up 480ms ${i * 80}ms cubic-bezier(0.32,0.72,0,1) backwards, fc-float ${
                    3 + i * 0.5
                  }s ${i * 200}ms ease-in-out ${480 + i * 80}ms infinite`,
            }}
          >
            <ResponsiveAsset
              src={face.imageUrl}
              alt=""
              loading="eager"
              widthHint={80}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        ))}
      </div>
      <div className="relative z-10 flex flex-col items-center gap-3 text-center">
        <h1
          className="text-2xl font-semibold leading-tight"
          style={{ color: "hsl(var(--brand-bg-foreground))" }}
        >
          {headline}
        </h1>
        {description && (
          <p className="max-w-sm text-sm" style={{ color: "hsl(var(--hero-fg-muted))" }}>
            {description}
          </p>
        )}
      </div>
    </section>
  );
};

export const AnticipationScreen = memo(AnticipationScreenImpl);

export default AnticipationScreen;
